// Engagement popup localStorage tracking utilities

const PREFIX = "outspoken_";

export const KEYS = {
  NEWSLETTER_COUNT: `${PREFIX}newsletter_popup_count`,
  NEWSLETTER_LAST: `${PREFIX}newsletter_popup_last`,
  NEWSLETTER_SUBSCRIBED: `${PREFIX}newsletter_subscribed`,
  UPGRADE_COUNT: `${PREFIX}upgrade_popup_count`,
  UPGRADE_LAST: `${PREFIX}upgrade_popup_last`,
  SOCIAL_LAST: `${PREFIX}social_popup_last`,
  SOCIAL_COUNT: `${PREFIX}social_popup_count`,
  PAGE_VISITS: `${PREFIX}page_visits`,
  SESSION_COUNT: `${PREFIX}session_count`,
  FIRST_VISIT: `${PREFIX}first_visit`,
} as const;

export function getNum(key: string): number {
  return parseInt(localStorage.getItem(key) || "0", 10);
}

export function setNum(key: string, val: number) {
  localStorage.setItem(key, String(val));
}

export function getTimestamp(key: string): number {
  return parseInt(localStorage.getItem(key) || "0", 10);
}

export function setTimestamp(key: string) {
  localStorage.setItem(key, String(Date.now()));
}

export function getBool(key: string): boolean {
  return localStorage.getItem(key) === "true";
}

export function setBool(key: string, val: boolean) {
  localStorage.setItem(key, String(val));
}

/** Check if enough days have passed since the last popup show */
function daysSince(key: string): number {
  const last = getTimestamp(key);
  if (!last) return Infinity;
  return (Date.now() - last) / (1000 * 60 * 60 * 24);
}

/** Should we show the newsletter popup? */
export function shouldShowNewsletter(): boolean {
  if (getBool(KEYS.NEWSLETTER_SUBSCRIBED)) return false;
  const count = getNum(KEYS.NEWSLETTER_COUNT);
  if (count >= 3) {
    // Reset after 30 days
    if (daysSince(KEYS.NEWSLETTER_LAST) > 30) {
      setNum(KEYS.NEWSLETTER_COUNT, 0);
      return true;
    }
    return false;
  }
  // Cooldown: at least 2 days between shows
  if (daysSince(KEYS.NEWSLETTER_LAST) < 2) return false;
  return true;
}

/** Should we show the upgrade popup? (free users only) */
export function shouldShowUpgrade(): boolean {
  const count = getNum(KEYS.UPGRADE_COUNT);
  if (count >= 10) return false;
  // Cooldown: 3 days
  if (daysSince(KEYS.UPGRADE_LAST) < 3) return false;
  // Trigger conditions: 2+ sessions or 3+ page visits
  const sessions = getNum(KEYS.SESSION_COUNT);
  const visits = getNum(KEYS.PAGE_VISITS);
  return sessions >= 2 || visits >= 3;
}

/** Should we show the social/feedback popup? */
export function shouldShowSocial(): boolean {
  const count = getNum(KEYS.SOCIAL_COUNT);
  if (count >= 5) return false;
  // Cooldown: 7 days
  if (daysSince(KEYS.SOCIAL_LAST) < 7) return false;
  // Need at least 1 session
  return getNum(KEYS.SESSION_COUNT) >= 1;
}

export function recordNewsletterShown() {
  setNum(KEYS.NEWSLETTER_COUNT, getNum(KEYS.NEWSLETTER_COUNT) + 1);
  setTimestamp(KEYS.NEWSLETTER_LAST);
}

export function recordUpgradeShown() {
  setNum(KEYS.UPGRADE_COUNT, getNum(KEYS.UPGRADE_COUNT) + 1);
  setTimestamp(KEYS.UPGRADE_LAST);
}

export function recordSocialShown() {
  setNum(KEYS.SOCIAL_COUNT, getNum(KEYS.SOCIAL_COUNT) + 1);
  setTimestamp(KEYS.SOCIAL_LAST);
}

export function markNewsletterSubscribed() {
  setBool(KEYS.NEWSLETTER_SUBSCRIBED, true);
}

export function recordPageVisit() {
  setNum(KEYS.PAGE_VISITS, getNum(KEYS.PAGE_VISITS) + 1);
}

export function incrementSessionCount() {
  setNum(KEYS.SESSION_COUNT, getNum(KEYS.SESSION_COUNT) + 1);
}
